import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import FloatingCTA from "@/components/feature/FloatingCTA";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SEO from "@/components/feature/SEO";
import { localBusinessSchema, webpageSchema, breadcrumbSchema, siteUrl } from "@/lib/schemas";
import { supabase, isSupabaseConnected, supabaseFunctionsBaseUrl, supabaseAnonKeyClean } from "@/lib/supabase";
import { trackEvent } from "@/lib/gtag";
import { SupabaseConnectionStatus } from "@/components/feature/SupabaseConnectionStatus";
import WhatsAppWidget from "@/components/feature/WhatsAppWidget";
// ... existing imports ...
import { Phone, Mail, MapPin, Clock, MessageSquare, ArrowRight, User, CheckCircle, FileText, Send } from "lucide-react";

export default function ContactPage() {
  const baseUrl = siteUrl;
  const pageUrl = `${baseUrl}/contact`;

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    insurance_type: "health",
    notes: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError("");
    console.log("[Contact Form] Submit started");

    if (!isSupabaseConnected || !supabase) {
      console.error("[Contact Form] Supabase NOT connected — isSupabaseConnected:", isSupabaseConnected, "supabase:", !!supabase);
      setSubmitting(false);
      setSubmitError("Unable to connect to our system right now. Please call us at +91 99993 65655 or WhatsApp us.");
      return;
    }

    console.log("[Contact Form] Supabase connected, attempting insert...");
    try {
      const { data: insertData, error } = await supabase.from("leads").insert({
        name: formData.name,
        phone: formData.phone,
        email: formData.email || null,
        city: formData.city || null,
        insurance_type: formData.insurance_type,
        notes: formData.notes || null,
        source: "Contact Page",
        status: "new",
      }).select();

      console.log("[Contact Form] Insert result:", { insertData, error });

      if (error) {
        console.error("[Contact Form] Insert error:", error);
        setSubmitting(false);
        setSubmitError(error.message || "Something went wrong saving your request. Please try again or call +91 99993 65655.");
        return;
      }

      console.log("[Contact Form] Insert succeeded, data:", insertData);

      // Notify admins via edge function (fire-and-forget)
      try {
        const edgeUrl = `${supabaseFunctionsBaseUrl}/functions/v1/send-lead-notification`;
        await fetch(edgeUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${supabaseAnonKeyClean}`,
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            city: formData.city,
            insurance_type: formData.insurance_type,
            source: "Contact Page",
            notes: formData.notes,
          }),
        });
      } catch {
        // Silently ignore notification failures
      }

      setSubmitting(false);
      setSubmitted(true);
      trackEvent("submit_lead", {
        insurance_type: formData.insurance_type,
        page_location: window.location.href,
        source: "Contact Page",
        value: 1,
        currency: "INR",
      });
    } catch (err) {
      console.error("[Contact Form] Exception during submit:", err);
      setSubmitting(false);
      setSubmitError("Network error. Please check your connection and try again, or call +91 99993 65655.");
    }
  };

  const schemas = [
    webpageSchema(
      "Contact ExpertAdvise.in | Insurance & Financial Advisory Support",
      "Contact ExpertAdvise.in for insurance advice, claims support, and financial planning. Call +91 99993 65655, WhatsApp, or email info@expertadvise.in. Mon-Sat 9AM-8PM.",
      pageUrl
    ),
    localBusinessSchema(),
    breadcrumbSchema([
      { name: "Home", url: baseUrl },
      { name: "Contact", url: pageUrl },
    ]),
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title="Contact ExpertAdvise.in | Insurance & Financial Advisory Support"
        description="Contact ExpertAdvise.in for insurance advice, claims support, and financial planning. Call +91 99993 65655, WhatsApp, or email info@expertadvise.in. Mon-Sat 9AM-8PM."
        keywords="contact insurance advisor, insurance helpline India, ExpertAdvise phone, insurance consultant contact, financial planner India"
        canonical={pageUrl}
        ogUrl={pageUrl}
        schemas={schemas}
      />
      <Navbar />

      <section className="pt-24 md:pt-32 pb-10 md:pb-14 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <Breadcrumbs
              items={[
                { name: "Home", href: "/" },
                { name: "Contact" },
              ]}
              className="justify-center mb-4"
            />
            <span className="inline-block bg-primary-50 text-primary-700 text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full mb-3">
              Get in Touch
            </span>
            <h1 className="text-2xl md:text-4xl font-bold text-slate-900 mb-4">
              Contact ExpertAdvise.in
            </h1>
            <p className="text-slate-600 text-base md:text-lg leading-relaxed">
              Have questions about insurance, investments, or financial planning? 
              Reach out to us through any channel — we respond within 2 hours during business hours.
            </p>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-6 mb-10">
            {[
              { icon: Phone, title: "Phone", value: "+91 99993 65655", href: "tel:+919999365655", color: "bg-emerald-50 text-emerald-600", desc: "Mon-Sat, 9 AM - 8 PM" },
              { icon: Mail, title: "Email", value: "info@expertadvise.in", href: "mailto:info@expertadvise.in", color: "bg-blue-50 text-blue-600", desc: "We reply within 2 hours" },
              { icon: MessageSquare, title: "WhatsApp", value: "+91 99993 65655", href: "https://wa.me/919999365655", color: "bg-green-50 text-green-600", desc: "Instant messaging support" },
            ].map((item) => (
              <a key={item.title} href={item.href} target={item.title === "WhatsApp" ? "_blank" : undefined} rel="noopener noreferrer" className="group bg-white rounded-2xl border border-slate-200 p-6 hover:border-primary-200 hover:shadow-lg transition-all duration-300">
                <div className={`w-12 h-12 rounded-xl ${item.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <item.icon className="w-6 h-6" />
                </div>
                <div className="text-sm text-slate-500 mb-1">{item.title}</div>
                <div className="text-lg font-bold text-slate-900 mb-1 group-hover:text-primary-700 transition-colors">{item.value}</div>
                <div className="text-xs text-slate-400">{item.desc}</div>
              </a>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="p-6 md:p-8">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Office Details</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-primary-500 shrink-0 mt-0.5" />
                    <div>
                      <div className="text-sm font-semibold text-slate-900">Head Office</div>
                      <div className="text-sm text-slate-500">Delhi NCR, India</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-primary-500 shrink-0 mt-0.5" />
                    <div>
                      <div className="text-sm font-semibold text-slate-900">Business Hours</div>
                      <div className="text-sm text-slate-500">Monday - Saturday: 9:00 AM - 8:00 PM IST<br />Sunday: 10:00 AM - 2:00 PM IST</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-primary-500 shrink-0 mt-0.5" />
                    <div>
                      <div className="text-sm font-semibold text-slate-900">Toll-Free & Support</div>
                      <div className="text-sm text-slate-500">+91 99993 65655 (Primary)<br />WhatsApp: +91 99993 65655</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-slate-100 p-6 md:p-8 bg-slate-50">
                <h4 className="text-sm font-semibold text-slate-900 mb-3">We Serve Across India</h4>
                <div className="flex flex-wrap gap-2">
                  {["Delhi", "Noida", "Gurgaon", "Ghaziabad", "Faridabad", "Mumbai", "Pune", "Bangalore", "Hyderabad", "Chennai", "Ahmedabad", "Jaipur", "Lucknow", "Chandigarh"].map((city) => (
                    <span key={city} className="text-xs bg-white border border-slate-200 rounded-full px-3 py-1 text-slate-600">{city}</span>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8">
              <SupabaseConnectionStatus />
              <div className="mt-4">
                {submitted ? (
                  <div className="text-center py-6">
                    <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-7 h-7 text-emerald-600" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">Quote Request Sent!</h3>
                    <p className="text-sm text-slate-600 mb-4">Thank you, {formData.name}. Our advisor will call you within 2 hours.</p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                      <WhatsAppWidget
                        variant="success"
                        userPhone={formData.phone}
                        insuranceType={formData.insurance_type}
                      >
                        Chat on WhatsApp
                      </WhatsAppWidget>
                      <a
                        href="tel:+919999365655"
                        className="inline-flex items-center gap-2 bg-cta-600 hover:bg-cta-700 text-white font-semibold px-5 py-2.5 rounded-full transition-colors whitespace-nowrap"
                      >
                        <Phone className="w-4 h-4" />
                        Call Now
                      </a>
                    </div>
                    <p className="text-xs text-slate-400 mt-3">
                      Or WhatsApp us directly at +91 99993 65655
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <h3 className="text-lg font-bold text-slate-900 mb-1">Request Free Quote</h3>
                      <p className="text-sm text-slate-500 mb-4">Get personalized insurance recommendations from our experts.</p>
                    </div>

                    {submitError && (
                      <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
                        <div className="font-semibold mb-1">Submission failed</div>
                        <div className="text-xs font-mono break-all">{submitError}</div>
                      </div>
                    )}

                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1.5">
                        <User className="w-4 h-4 text-primary-500" />Full Name *
                      </label>
                      <input required name="name" value={formData.name} onChange={handleChange} type="text" placeholder="Enter your full name" className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-900 outline-none focus:border-primary-400 placeholder:text-slate-400" />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1.5">
                          <Phone className="w-4 h-4 text-primary-500" />Mobile *
                        </label>
                        <input required name="phone" value={formData.phone} onChange={handleChange} type="tel" placeholder="+91 99999 99999" className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-900 outline-none focus:border-primary-400 placeholder:text-slate-400" />
                      </div>
                      <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1.5">
                          <Mail className="w-4 h-4 text-primary-500" />Email
                        </label>
                        <input name="email" value={formData.email} onChange={handleChange} type="email" placeholder="you@example.com" className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-900 outline-none focus:border-primary-400 placeholder:text-slate-400" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1.5">
                          <MapPin className="w-4 h-4 text-primary-500" />City
                        </label>
                        <input name="city" value={formData.city} onChange={handleChange} type="text" placeholder="e.g. Delhi" className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-900 outline-none focus:border-primary-400 placeholder:text-slate-400" />
                      </div>
                      <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1.5">
                          <FileText className="w-4 h-4 text-primary-500" />Insurance Type *
                        </label>
                        <select required name="insurance_type" value={formData.insurance_type} onChange={handleChange} className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-900 outline-none focus:border-primary-400 cursor-pointer">
                          <option value="health">Health Insurance</option>
                          <option value="term">Term Insurance</option>
                          <option value="life">Life Insurance</option>
                          <option value="car">Car Insurance</option>
                          <option value="travel">Travel Insurance</option>
                          <option value="corporate">Corporate / Group Health</option>
                          <option value="mutual-funds">Mutual Funds / SIP</option>
                          <option value="retirement">Retirement Planning</option>
                          <option value="financial">Financial Planning</option>
                          <option value="multiple">Multiple / Not Sure</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1.5">
                        <MessageSquare className="w-4 h-4 text-primary-500" />Message (Optional)
                      </label>
                      <textarea name="notes" value={formData.notes} onChange={handleChange} rows={3} maxLength={500} placeholder="Tell us about your requirements..." className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-900 outline-none focus:border-primary-400 placeholder:text-slate-400 resize-none" />
                      <div className="text-xs text-slate-400 mt-1 text-right">{formData.notes.length}/500</div>
                    </div>

                    <button type="submit" disabled={submitting} className="w-full bg-cta-600 hover:bg-cta-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold px-6 py-3 rounded-xl transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer">
                      {submitting ? (
                        <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      ) : (
                        <>Get Free Quote <Send className="w-4 h-4" /></>
                      )}
                    </button>

                    <p className="text-xs text-slate-400 text-center">
                      By submitting, you agree to our{" "}
                      <Link to="/privacy-policy" className="text-primary-600 hover:underline">Privacy Policy</Link>{" "}
                      and{" "}
                      <Link to="/terms" className="text-primary-600 hover:underline">Terms of Service</Link>.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingCTA />
    </div>
  );
}